// FOUCPreventionSystem.js
// Prevents Flash of Unstyled Content during scene transitions
// Enterprise-grade CSS loading and content visibility management

/**
 * FOUC Prevention System
 * Ensures CSS is fully loaded before content becomes visible
 * Prevents unprofessional flashing of unstyled content
 */
export class FOUCPreventionSystem {
    static _instance = null;
    
    /**
     * Singleton pattern
     */
    static getInstance() {
        if (!FOUCPreventionSystem._instance) {
            FOUCPreventionSystem._instance = new FOUCPreventionSystem();
        }
        return FOUCPreventionSystem._instance;
    }
    
    constructor() {
        if (FOUCPreventionSystem._instance) {
            return FOUCPreventionSystem._instance;
        }
        
        this.loadedStylesheets = new Set();
        this.pendingStylesheets = new Map();
        this.hiddenOverlays = new Map();
        
        FOUCPreventionSystem._instance = this;
        console.log('🎨 FOUC Prevention System initialized');
    }
    
    /**
     * Load CSS with promise-based tracking
     * @param {string} href - CSS file path
     * @param {string} id - Unique ID for the stylesheet
     * @returns {Promise<void>}
     */
    async loadCSS(href, id) {
        // Check if already loaded
        if (this.loadedStylesheets.has(id)) {
            console.log(`🎨 CSS already loaded: ${id}`);
            return Promise.resolve();
        }
        
        // Check if currently loading
        if (this.pendingStylesheets.has(id)) {
            console.log(`🎨 CSS already loading: ${id}, waiting...`);
            return this.pendingStylesheets.get(id);
        }
        
        // Check if link already exists in DOM
        const existing = document.getElementById(id);
        if (existing) {
            this.loadedStylesheets.add(id);
            console.log(`🎨 CSS link exists in DOM: ${id}`);
            return Promise.resolve();
        }
        
        console.log(`🎨 Loading CSS: ${id} from ${href}`);
        
        const loadPromise = new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            link.href = href;
            
            link.onload = () => {
                this.loadedStylesheets.add(id);
                this.pendingStylesheets.delete(id);
                console.log(`✅ CSS loaded successfully: ${id}`);
                resolve();
            };
            
            link.onerror = (error) => {
                this.pendingStylesheets.delete(id);
                console.error(`❌ CSS failed to load: ${id}`, error);
                reject(new Error(`Failed to load CSS: ${id}`));
            };
            
            document.head.appendChild(link);
        });
        
        this.pendingStylesheets.set(id, loadPromise);
        return loadPromise;
    }
    
    /**
     * Load multiple CSS files in parallel
     * @param {Array<{href: string, id: string}>} stylesheets
     * @returns {Promise<void[]>}
     */
    async loadCSSBundle(stylesheets) {
        console.log(`🎨 Loading CSS bundle: ${stylesheets.length} files`);
        const loadPromises = stylesheets.map(({ href, id }) => this.loadCSS(href, id));
        return Promise.all(loadPromises);
    }
    
    /**
     * Create overlay that's hidden until CSS loads
     * @param {Object} config - Overlay configuration
     * @returns {HTMLElement}
     */
    createHiddenOverlay(config) {
        const {
            id,
            cssText = '',
            innerHTML = '',
            stylesheets = []
        } = config;
        
        console.log(`🎨 Creating hidden overlay: ${id}`);
        
        // Remove existing overlay
        const existing = document.getElementById(id);
        if (existing) {
            existing.remove();
        }
        
        // Create overlay with hidden state
        const overlay = document.createElement('div');
        overlay.id = id;
        
        // CRITICAL: Start hidden to prevent FOUC
        overlay.style.cssText = cssText + `
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
        `;
        
        overlay.innerHTML = innerHTML;
        
        // Track hidden overlay
        this.hiddenOverlays.set(id, {
            element: overlay,
            stylesheets,
            revealed: false
        });
        
        return overlay;
    }
    
    /**
     * Reveal overlay after CSS has loaded
     * @param {string} overlayId - Overlay ID
     * @param {Array<{href: string, id: string}>} stylesheets - Required CSS files
     * @returns {Promise<void>}
     */
    async revealOverlay(overlayId, stylesheets = []) {
        const overlayData = this.hiddenOverlays.get(overlayId);
        if (!overlayData) {
            console.warn(`⚠️ Overlay not tracked: ${overlayId}`);
            // Try to find it in DOM anyway
            const overlay = document.getElementById(overlayId);
            if (overlay) {
                this._showOverlay(overlay);
            }
            return;
        }
        
        const { element } = overlayData;
        
        // If no stylesheets specified, use tracked ones
        const cssToLoad = stylesheets.length > 0 ? stylesheets : overlayData.stylesheets;
        
        console.log(`🎨 Revealing overlay: ${overlayId} after loading ${cssToLoad.length} stylesheets`);
        
        try {
            // Wait for all CSS to load
            if (cssToLoad.length > 0) {
                await this.loadCSSBundle(cssToLoad);
            }
            
            // Small delay to ensure CSS is applied
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Reveal overlay with smooth transition
            this._showOverlay(element);
            
            overlayData.revealed = true;
            console.log(`✅ Overlay revealed: ${overlayId}`);
            
        } catch (error) {
            console.error(`❌ Error revealing overlay: ${overlayId}`, error);
            // Show anyway to prevent blank screen
            this._showOverlay(element);
        }
    }
    
    /**
     * Show overlay with smooth transition
     * @private
     */
    _showOverlay(element) {
        // Remove FOUC prevention styles
        element.style.visibility = 'visible';
        element.style.pointerEvents = 'auto';
        
        // Smooth fade-in
        requestAnimationFrame(() => {
            element.style.transition = 'opacity 0.3s ease-out';
            element.style.opacity = '1';
        });
    }
    
    /**
     * Create and reveal overlay (convenience method)
     * @param {Object} config - Overlay configuration
     * @returns {Promise<HTMLElement>}
     */
    async createAndRevealOverlay(config) {
        const overlay = this.createHiddenOverlay(config);
        document.body.appendChild(overlay);
        
        await this.revealOverlay(config.id, config.stylesheets);
        
        return overlay;
    }
    
    /**
     * Preload CSS files before they're needed
     * @param {Array<{href: string, id: string}>} stylesheets
     */
    async preloadCSS(stylesheets) {
        console.log(`🎨 Preloading ${stylesheets.length} CSS files`);
        
        const preloadPromises = stylesheets.map(({ href, id }) => {
            // Use link preload for faster loading
            const preload = document.createElement('link');
            preload.rel = 'preload';
            preload.as = 'style';
            preload.href = href;
            
            return new Promise((resolve) => {
                preload.onload = () => {
                    // Now load as actual stylesheet
                    this.loadCSS(href, id).then(resolve);
                };
                preload.onerror = resolve; // Continue even if preload fails
                document.head.appendChild(preload);
            });
        });
        
        await Promise.all(preloadPromises);
        console.log(`✅ Preloaded ${stylesheets.length} CSS files`);
    }
    
    /**
     * Set transition background to prevent white flashes
     * @param {string} color - Background color
     */
    setTransitionBackground(color = '#0f172a') {
        document.body.style.background = color;
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.background = color;
        }
        console.log(`🎨 Set transition background: ${color}`);
    }
    
    /**
     * Clear tracked overlay
     * @param {string} overlayId
     */
    clearOverlay(overlayId) {
        this.hiddenOverlays.delete(overlayId);
        console.log(`🧹 Cleared overlay tracking: ${overlayId}`);
    }
    
    /**
     * Helper method to replace innerHTML with FOUC prevention
     * Hides element, sets content, waits for CSS, then reveals
     * @param {HTMLElement} element - Element to update
     * @param {string} htmlContent - HTML content to set
     * @param {Array<{href: string, id: string}>} stylesheets - Required CSS files
     * @returns {Promise<void>}
     */
    async setHTMLWithFOUCPrevention(element, htmlContent, stylesheets = []) {
        console.log(`🎨 Setting HTML with FOUC prevention for element: ${element.id || 'unnamed'}`);
        
        // CRITICAL: Preserve element dimensions to prevent layout shift
        const rect = element.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(element);
        const minHeight = computedStyle.minHeight;
        
        // Set minimum height to maintain layout during transition
        if (rect.height > 0) {
            element.style.minHeight = `${rect.height}px`;
        }
        
        // Hide with opacity only (not visibility) to prevent CLS
        const originalOpacity = element.style.opacity;
        element.style.opacity = '0';
        element.style.transition = 'opacity 0.2s ease-out';
        
        // Wait for fade out
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Set content while hidden
        element.innerHTML = htmlContent;
        
        // Load CSS if specified
        if (stylesheets.length > 0) {
            await this.loadCSSBundle(stylesheets);
            // Small delay to ensure CSS is applied
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Force browser to calculate layout with new content
        element.offsetHeight; // Force reflow
        
        // Reveal element with smooth fade-in
        requestAnimationFrame(() => {
            element.style.opacity = originalOpacity || '1';
        });
        
        // Wait for fade in, then restore original minHeight
        setTimeout(() => {
            if (minHeight) {
                element.style.minHeight = minHeight;
            } else {
                element.style.minHeight = '';
            }
        }, 200);
        
        console.log(`✅ HTML set with FOUC prevention for: ${element.id || 'unnamed'}`);
    }
    
    /**
     * Get system status
     */
    getStatus() {
        return {
            loadedStylesheets: Array.from(this.loadedStylesheets),
            pendingStylesheets: Array.from(this.pendingStylesheets.keys()),
            hiddenOverlays: Array.from(this.hiddenOverlays.keys()),
            totalStylesheets: this.loadedStylesheets.size
        };
    }
}

// Export singleton instance
export const foucPrevention = FOUCPreventionSystem.getInstance();

// Global access for debugging
if (typeof window !== 'undefined') {
    window.foucPrevention = foucPrevention;
    console.log('🎨 FOUC Prevention System available as window.foucPrevention');
}

